import * as React from 'react'

import { Controller, UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Field, Text } from '@chakra-ui/react'

import {
  Label,
  Link,
  Select,
  SelectOptionInterface,
  Term,
} from '@enechain/ecloud-designsystem'
import { FaArrowUpRightFromSquareRegular } from '@enechain/ecloud-icons'

import { Language, License, UserRoleSetting } from '~/gen/proto/bff/v1/model_pb'
import { useLoginUser } from '~/hooks/useLoginUser'
import { MemberEditSchema } from '~/pages/Members/components/MemberEdit/memberEditSchema'

type UserRoleOption = SelectOptionInterface &
  MemberEditSchema['userRoleSettings'][number][number]

type Props = {
  formMethods: UseFormReturn<MemberEditSchema>
  userRoleSetting: UserRoleSetting[]
  index: number
  license: License
}

export const UserRoleSelect: React.FC<Props> = ({
  formMethods,
  userRoleSetting,
  license,
  index,
}) => {
  const { t } = useTranslation()
  const {
    formState: { errors },
  } = formMethods
  const me = useLoginUser()
  const isEng = me?.language === Language.ENGLISH
  const { planCode, planId, planName, engPlanName, productName } = license
  const options = userRoleSetting.map((setting) => ({
    label: isEng ? setting.userRoleEngName : setting.userRoleName,
    value: setting.userRoleCode,
    planCode,
    isSetting: setting.isSetting,
    isDefault: setting.isDefault,
  }))
  const settingRoles = options.filter((setting) => setting.isSetting)
  const defaultValue =
    settingRoles.length > 0
      ? settingRoles
      : options.filter((setting) => setting.isDefault)

  return (
    <Controller
      control={formMethods.control}
      defaultValue={defaultValue}
      name={`userRoleSettings.${index}`}
      render={({ field: { onChange, value } }): React.ReactElement => (
        <Field.Root
          hidden={
            license.isUserRoleConfigurable === false ||
            license.isProductPublic === false
          }
          invalid={
            errors.userRoleSettings &&
            errors.userRoleSettings[index] !== undefined
          }
          alignItems="stretch"
        >
          <Term>
            <Label
              required
            >{`${productName} / ${isEng ? engPlanName : planName}`}</Label>
            <Select<UserRoleOption, true>
              isMulti
              onChange={onChange}
              options={options}
              selectedOptions={value}
            />
            <Link
              alignItems="center"
              gap="1"
              display="flex"
              href={`/user_roles/${planId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Text>{t('pages.members.link.user_role_page')}</Text>
              <FaArrowUpRightFromSquareRegular />
            </Link>

            {errors.userRoleSettings && (
              <Field.ErrorText>
                {errors.userRoleSettings[index]?.message}
              </Field.ErrorText>
            )}
          </Term>
        </Field.Root>
      )}
    />
  )
}
